import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import * as API from '@vantiva-sa/humarestclient/dist';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { getResourcesQueryKey } from '../Hooks/useResourcesByType';
import { GalleryResourceTypes, GalleryUploadStatus } from '../../constants/galleryUpload';
import UploadProgressTray from './UploadProgressTray';

const RESOURCE_CONFIG = {
  [GalleryResourceTypes.PHOTOS]: {
    defaultContentType: 'image/jpeg',
    translationPrefix: 'GALLERY_PHOTO_UPLOAD',
  },
  [GalleryResourceTypes.VIDEOS]: {
    defaultContentType: 'video/mp4',
    translationPrefix: 'GALLERY_VIDEO_UPLOAD',
  },
};

const MAX_CHECKSUM_FILE_SIZE_BYTES = 100 * 1024 * 1024;

const GalleryUploadContext = createContext(null);

const bytesToHex = (bytes) => {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const computeSha256Checksum = async (file) => {
  if (
    !file ||
    typeof file.arrayBuffer !== 'function' ||
    !globalThis?.crypto?.subtle ||
    file.size > MAX_CHECKSUM_FILE_SIZE_BYTES
  ) {
    return '';
  }

  const fileBuffer = await file.arrayBuffer();
  const digest = await globalThis.crypto.subtle.digest('SHA-256', fileBuffer);
  return bytesToHex(new Uint8Array(digest));
};

const sanitizeFileNameForApi = (name = '') => {
  const sanitizedName = String(name)
    .trim()
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');

  return sanitizedName || 'file';
};

const getFileNameWithoutExtension = (name = '') => name.replace(/\.[^/.]+$/, '');

const getEncodedUploadFileName = (name = '') => {
  return encodeURIComponent(name).replace(
    /[!'()*]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`
  );
};

const resolveUploadUrl = (responseData) =>
  responseData?.uploadurl ||
  responseData?.uploadURL ||
  responseData?.presignedUrl ||
  responseData?.presignedURL ||
  '';

const getImageMetadata = (file) =>
  new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      image.onload = null;
      image.onerror = null;
    };

    image.onload = () => {
      const resolution = image.width && image.height ? `${image.width}X${image.height}` : '';
      cleanup();
      resolve({ resolution });
    };

    image.onerror = () => {
      cleanup();
      resolve({ resolution: '' });
    };

    image.src = objectUrl;
  });

const getVideoMetadata = (file) =>
  new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.src = '';
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? Math.round(video.duration) : undefined;
      const resolution =
        video.videoWidth && video.videoHeight ? `${video.videoWidth}X${video.videoHeight}` : '';
      cleanup();
      resolve({ duration, resolution });
    };

    video.onerror = () => {
      cleanup();
      resolve({ duration: undefined, resolution: '' });
    };

    video.src = objectUrl;
  });

const uploadFileToPresignedUrl = ({ uploadUrl, file, contentType, onProgress }) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || typeof onProgress !== 'function') {
        return;
      }

      onProgress((event.loaded / event.total) * 100);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }

      reject(new Error(`S3 upload failed with status ${xhr.status}`));
    };

    xhr.onerror = () => reject(new Error('S3 upload failed due to a network error'));
    xhr.onabort = () => reject(new Error('S3 upload aborted'));

    xhr.send(file);
  });

export const GalleryUploadProvider = ({ children }) => {
  const trans = useSelector(translator);
  const currentUser = useSelector(getCurrentUser);
  const uploadResourceApi = useSelector((state) => state.apis.uploadResourceApi);
  const shareResourcesApi = useSelector((state) => state.apis.shareResourcesApi);
  const queryClient = useQueryClient();

  const [uploadProgressItems, setUploadProgressItems] = useState([]);

  const fallbackUploadApi = useMemo(() => new API.UploadResourcesApi(), []);
  const fallbackShareApi = useMemo(() => new API.ShareResourcesApi(), []);

  const uploadApi =
    uploadResourceApi &&
    typeof uploadResourceApi.getResourceUploadURL === 'function' &&
    typeof uploadResourceApi.uploadResources === 'function'
      ? uploadResourceApi
      : fallbackUploadApi;

  const shareApi =
    shareResourcesApi && typeof shareResourcesApi.shareResources === 'function'
      ? shareResourcesApi
      : fallbackShareApi;

  const isUploadApiAvailable =
    !!uploadApi &&
    typeof uploadApi.getResourceUploadURL === 'function' &&
    typeof uploadApi.uploadResources === 'function';

  const isShareApiAvailable = !!shareApi && typeof shareApi.shareResources === 'function';

  const uploaderUuid = currentUser?.useruuid;

  const dismissUploadProgressItems = useCallback((uploadIds = []) => {
    if (!uploadIds.length) {
      return;
    }

    const idsToRemove = new Set(uploadIds);

    setUploadProgressItems((previousItems) =>
      previousItems.filter((uploadItem) => !idsToRemove.has(uploadItem.id))
    );
  }, []);

  const upsertUploadProgressItem = useCallback((uploadId, patch) => {
    setUploadProgressItems((previousItems) => {
      const itemIndex = previousItems.findIndex((uploadItem) => uploadItem.id === uploadId);

      if (itemIndex === -1) {
        return [...previousItems, { id: uploadId, ...patch }];
      }

      const nextItems = [...previousItems];
      nextItems[itemIndex] = {
        ...nextItems[itemIndex],
        ...patch,
      };

      return nextItems;
    });
  }, []);

  const requestUploadUrl = useCallback(
    (fileName, resourceType) =>
      new Promise((resolve, reject) => {
        uploadApi.getResourceUploadURL(
          uploaderUuid,
          resourceType,
          fileName,
          apiCallback({
            translator: trans,
            successCallback: resolve,
            failCallback: reject,
            errorCallback: reject,
          })
        );
      }),
    [uploadApi, uploaderUuid, trans]
  );

  const publishResource = useCallback(
    (resourceType, requestBody) =>
      new Promise((resolve, reject) => {
        uploadApi.uploadResources(
          uploaderUuid,
          resourceType,
          requestBody,
          apiCallback({
            translator: trans,
            successCallback: resolve,
            failCallback: reject,
            errorCallback: reject,
          })
        );
      }),
    [uploadApi, uploaderUuid, trans]
  );

  const shareResources = useCallback(
    (principalUuid, resourceType, resourceuuids) =>
      new Promise((resolve, reject) => {
        shareApi.shareResources(
          principalUuid,
          'share',
          resourceType,
          { resourceuuid: resourceuuids },
          apiCallback({
            translator: trans,
            successCallback: resolve,
            failCallback: reject,
            errorCallback: reject,
          })
        );
      }),
    [shareApi, trans]
  );

  const uploadSingleFile = useCallback(
    async ({ file, resourceType, title, description, onProgress }) => {
      const fileName = file.name;
      const sanitizedFileName = sanitizeFileNameForApi(fileName);
      const encodedFileName = getEncodedUploadFileName(sanitizedFileName);
      const uploadInfo = await requestUploadUrl(encodedFileName, resourceType);
      const uploadUrl = resolveUploadUrl(uploadInfo);

      if (!uploadUrl || !uploadInfo?.resourceuuid) {
        throw new Error('Invalid upload URL response');
      }

      const contentType = file.type || RESOURCE_CONFIG[resourceType].defaultContentType;

      await uploadFileToPresignedUrl({
        uploadUrl,
        file,
        contentType,
        onProgress,
      });

      const metadataResult =
        resourceType === GalleryResourceTypes.VIDEOS
          ? await getVideoMetadata(file)
          : await getImageMetadata(file);
      const checksum = await computeSha256Checksum(file);
      const normalizedTitle = title.trim() || getFileNameWithoutExtension(fileName);

      await publishResource(resourceType, {
        resourceuuid: uploadInfo.resourceuuid,
        filename: encodedFileName,
        title: normalizedTitle,
        ...(checksum ? { checksum } : {}),
        provider: 'HomeSight',
        metadata: {
          format: contentType,
          resolution: metadataResult.resolution,
          description: description.trim(),
          ...(resourceType === GalleryResourceTypes.VIDEOS
            ? { duration: metadataResult.duration }
            : {}),
        },
      });

      return uploadInfo.resourceuuid;
    },
    [publishResource, requestUploadUrl]
  );

  const startUploads = useCallback(
    async ({ selectedFiles, resourceType, accountUuid, title, description }) => {
      if (
        selectedFiles.length === 0 ||
        !isUploadApiAvailable ||
        !isShareApiAvailable ||
        !uploaderUuid ||
        !accountUuid
      ) {
        return;
      }

      const uploadBatchItems = selectedFiles.map((fileItem) => ({
        id: fileItem.id,
        fileName: fileItem.file?.name || '',
        progress: 0,
        status: GalleryUploadStatus.QUEUED,
        errorMessage: '',
      }));
      const uploadBatchIds = uploadBatchItems.map((item) => item.id);

      setUploadProgressItems((previousItems) => {
        const mergedById = new Map(previousItems.map((item) => [item.id, item]));

        uploadBatchItems.forEach((item) => {
          mergedById.set(item.id, {
            ...(mergedById.get(item.id) || {}),
            ...item,
          });
        });

        return Array.from(mergedById.values());
      });

      const uploadResults = await Promise.allSettled(
        selectedFiles.map(async (fileItem) => {
          upsertUploadProgressItem(fileItem.id, {
            fileName: fileItem.file?.name || '',
            progress: 0,
            status: GalleryUploadStatus.UPLOADING,
            errorMessage: '',
          });

          const resourceuuid = await uploadSingleFile({
            file: fileItem.file,
            resourceType,
            title,
            description,
            onProgress: (progressPercent) => {
              upsertUploadProgressItem(fileItem.id, {
                progress: progressPercent,
                status: GalleryUploadStatus.UPLOADING,
                errorMessage: '',
              });
            },
          });

          upsertUploadProgressItem(fileItem.id, {
            progress: 100,
            status: GalleryUploadStatus.SUCCESS,
            errorMessage: '',
          });

          return {
            resourceuuid,
            fileName: fileItem.file?.name || '',
          };
        })
      );

      const successfulUploads = uploadResults
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value)
        .filter((result) => !!result?.resourceuuid);

      uploadResults.forEach((result, index) => {
        if (result.status !== 'rejected') {
          return;
        }

        const failedFileItem = selectedFiles[index];

        if (!failedFileItem) {
          return;
        }

        const fallbackError = trans(`${RESOURCE_CONFIG[resourceType].translationPrefix}_ERROR`);

        upsertUploadProgressItem(failedFileItem.id, {
          status: GalleryUploadStatus.FAILED,
          errorMessage: result.reason?.message || fallbackError,
        });
      });

      const uploadedResourceUuids = successfulUploads
        .map((result) => result.resourceuuid)
        .filter(Boolean);

      if (uploadedResourceUuids.length > 0) {
        await shareResources(accountUuid, resourceType, uploadedResourceUuids);
      }

      if (successfulUploads.length > 0) {
        const uploadedFileNames = successfulUploads
          .map((result) => result.fileName)
          .filter(Boolean)
          .join(', ');

        message.success({
          content: trans('GALLERY_UPLOAD_SUCCESS_WITH_FILES').replace(
            '{{files}}',
            uploadedFileNames
          ),
          onClose: () => dismissUploadProgressItems(uploadBatchIds),
        });
      }

      const failedUploadsCount = uploadResults.length - successfulUploads.length;
      if (failedUploadsCount > 0) {
        message.error({
          content: trans(`${RESOURCE_CONFIG[resourceType].translationPrefix}_ERROR`),
          onClose: () => dismissUploadProgressItems(uploadBatchIds),
        });
      }

      if (accountUuid && uploadedResourceUuids.length > 0) {
        const queryKey = getResourcesQueryKey({
          resourceType,
          useruuid: accountUuid,
          shared: false,
        });

        await queryClient.invalidateQueries({ queryKey });
        await queryClient.refetchQueries({ queryKey, type: 'active' });
      }

      return {
        successCount: successfulUploads.length,
        failedCount: failedUploadsCount,
      };
    },
    [
      isShareApiAvailable,
      isUploadApiAvailable,
      queryClient,
      shareResources,
      trans,
      uploadSingleFile,
      uploaderUuid,
      dismissUploadProgressItems,
      upsertUploadProgressItem,
    ]
  );

  const contextValue = useMemo(
    () => ({
      startUploads,
      isUploadApiAvailable,
      isShareApiAvailable,
      uploaderUuid,
    }),
    [startUploads, isUploadApiAvailable, isShareApiAvailable, uploaderUuid]
  );

  return (
    <GalleryUploadContext.Provider value={contextValue}>
      {children}
      <UploadProgressTray trans={trans} uploads={uploadProgressItems} />
    </GalleryUploadContext.Provider>
  );
};

GalleryUploadProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useGalleryUpload = () => {
  const context = useContext(GalleryUploadContext);

  if (!context) {
    throw new Error('useGalleryUpload must be used within GalleryUploadProvider');
  }

  return context;
};
